import type { RadarBubbleLesson, CascadeTeacherEntry } from '@/services/api/teacher'
import { LessonList } from './LessonList'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

export interface TeacherSummary {
  contacted: number
  accepted: number
  declined: number
  pending: number
}

export interface CascadeTeachers {
  accepted?: CascadeTeacherEntry[]
  declined?: CascadeTeacherEntry[]
  candidates?: CascadeTeacherEntry[]
  overflow_count?: number
  // rk-fix-v2 (Root Cause 2): per-Lektion Interesse/Absage aus SubstitutionResponse
  // assigned (assigned-fix-v1): Lehrer mit tatsächlicher Zuweisung (grünes Badge)
  per_lesson?: Record<string, { interested: string[]; declined: string[]; assigned?: string[] }>
}

export interface AssignedTeacherEntry {
  code: string
  name?: string | null
}

export interface GlassTooltipContentProps {
  dotColor: string
  /** 'ring' = weisse Flaeche + farbiger Rand (incoming); 'solid' = farbige Flaeche. */
  dotVariant?: 'solid' | 'ring'
  headerLabel: string
  dateLabel: string
  /** Suffix nach dem Datum, z.B. " · 3 Lektionen" oder " Stunde 1". */
  countLabel?: string
  lessons: RadarBubbleLesson[]
  statusLabel?: string
  /** Rechte Footer-Spalte, z.B. "12.5h". */
  hoursLabel?: string
  /** Optionale Partial-Zeile, z.B. "2/5 offen". */
  partialLabel?: string
  /** Optionaler Prep-Hinweis, z.B. "Prep offen" / "Prep angefordert". */
  prepLabel?: string
  /** Roter Punkt wenn Prep angefordert (sonst amber). */
  prepUrgent?: boolean
  /** Direktor verlangt eine Handlung (zurückgestellt / Dokumente). */
  directorPending?: boolean
  /** Ungelesene Chat-Nachricht. */
  unreadChat?: boolean
  /** frost-v2 (E1/E6): Bubble hängt am Direktor-Gate (Frost). */
  gatePending?: boolean
  /** frost-v2 (E13): Frost-Chip-Text, ersetzt im Footer den Statuschip. */
  gateChipLabel?: string
  /** frost-v2 (E11): Zeile «Verteilung pausiert · Frist eingefroren (seit …)». */
  gateLineLabel?: string
  /** Optionaler Storno-Pending-Hinweis. */
  stornoPending?: boolean
  /** Optionaler Cascade-Lehrer-Zusammenfassung (Farbpunkte + Anzahlen). */
  teacherSummary?: TeacherSummary
  /** Split-Rolle der Bubble (yellow-fix-v2) - steuert Beschriftung der Cascade-Liste. */
  splitRole?: 'filled' | 'declined' | 'pending' | 'open' | 'cancelled' | 'expired' | 'interested' | 'assigned_elsewhere'
  /** Rollenspezifische Lehrer-Codes + Rang fuer den Hover (yellow-fix-v2, Punkt d). */
  cascadeTeachers?: CascadeTeachers
  /** assigned-fix-v1: Lehrer mit tatsächlicher Zuweisung (aus lessons[] abgeleitet) —
   *  Fallback, wenn keine Kaskaden-Daten existieren. Wird GRÜN dargestellt. */
  assignedTeachers?: AssignedTeacherEntry[]
  /** Optionale reine Fussnote, z.B. "Von: …" oder "3 Anfragen". */
  footerNote?: string
}

/**
 * Geteilter Tooltip-Inhalt für Radar (FloatingTooltip) UND Admin-Liste
 * (RequestListTooltip) UND das Hover-Panel unter dem Radar (HoveredBubbleInfo).
 * Single Source of Truth für das Hover-Layout, damit alle Oberflächen dasselbe
 * Design zeigen (gleiche Felder, gleiche Positionen).
 */
export function GlassTooltipContent({
  dotColor,
  dotVariant = 'solid',
  headerLabel,
  dateLabel,
  countLabel,
  lessons,
  statusLabel,
  hoursLabel,
  partialLabel,
  prepLabel,
  prepUrgent,
  directorPending,
  unreadChat,
  gatePending,
  gateChipLabel,
  gateLineLabel,
  stornoPending,
  teacherSummary,
  splitRole,
  cascadeTeachers,
  assignedTeachers,
  footerNote,
}: GlassTooltipContentProps) {
  const hasMultiple = lessons.length > 1
  const single = !hasMultiple ? lessons[0] : null
  // frost-v2 (E13): Frost-Chip ersetzt den Statuschip; Footer erscheint deshalb
  // auch dann, wenn statusLabel unterdrückt ist.
  const showFooter = !!(statusLabel || hoursLabel || (gatePending && gateChipLabel))
  const hasTeacherSummary = !!(
    teacherSummary &&
    (teacherSummary.contacted ||
      teacherSummary.accepted ||
      teacherSummary.declined ||
      teacherSummary.pending)
  )

  return (
    <div className="text-white">
      {/* Header: dot + label */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-2 h-2 rounded-full"
          style={
            dotVariant === 'ring'
              ? { background: '#ffffff', border: `2px solid ${dotColor}` }
              : { background: dotColor }
          }
        />
        <span className="text-xs font-medium text-gray-300">{headerLabel}</span>
      </div>

      {/* Date + count */}
      <div className="text-sm font-semibold mb-1">
        {dateLabel}
        {countLabel}
      </div>

      {/* Lesson list or single-line fallback */}
      {hasMultiple ? (
        <div className="mb-2">
          <LessonList lessons={lessons} compact />
        </div>
      ) : single ? (
        <div className="text-xs text-gray-400 mb-2">
          {single.subject_name || '—'}
          {single.klass_name && ` | ${single.klass_name}`}
          {single.room_name && ` | ${single.room_name}`}
        </div>
      ) : null}

      {/* Footer: status badge (left) + hours (right) — frost-v2: bei Gate
          ersetzt der Frost-Chip den Statuschip (E13), Stunden rechts bleiben. */}
      {showFooter && (
        <div className="flex items-center justify-between text-xs">
          {gatePending && gateChipLabel ? (
            <span
              className="px-1.5 py-0.5 rounded"
              style={{
                background: 'rgba(125,211,252,0.2)',
                border: '1px solid rgba(125,211,252,0.45)',
                color: '#7dd3fc',
              }}
            >
              {gateChipLabel}
            </span>
          ) : statusLabel ? (
            <span
              className="px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
            >
              {statusLabel}
            </span>
          ) : (
            <span />
          )}
          {hoursLabel && <span className="text-gray-400">{hoursLabel}</span>}
        </div>
      )}

      {/* frost-v2 (E11): Gate-Zeile — «Verteilung pausiert · Frist eingefroren
          seit {Datum}»; ohne gate_since fällt das «seit …» weg (R2-Fallback). */}
      {gatePending && gateLineLabel && (
        <div className="text-xs text-sky-300/90 mt-1">{gateLineLabel}</div>
      )}

      {/* Partial fulfillment */}
      {partialLabel && <div className="text-xs text-gray-400 mt-1">{partialLabel}</div>}

      {/* Prep status */}
      {prepLabel && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className={`w-2 h-2 rounded-full shrink-0 ${prepUrgent ? 'bg-red-500' : 'bg-amber-500'}`} />
          <span className={`text-xs font-medium ${prepUrgent ? 'text-red-400' : 'text-amber-400'}`}>{prepLabel}</span>
        </div>
      )}

      {directorPending && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
          <span className="text-xs text-indigo-300 font-medium">Direktor: Handlung nötig</span>
        </div>
      )}

      {unreadChat && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
          <span className="text-xs text-blue-300 font-medium">Neue Nachricht</span>
        </div>
      )}

      {/* Storno pending */}
      {stornoPending && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
          <span className="text-xs text-orange-400 font-medium">Storno angefragt</span>
        </div>
      )}

      {/* Cascade teacher summary */}
      {hasTeacherSummary && teacherSummary && (
        <div className="flex items-center gap-2 mt-1.5 text-xs">
          {teacherSummary.contacted > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-orange-400" />
              {teacherSummary.contacted} kontaktiert
            </span>
          )}
          {teacherSummary.accepted > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              {teacherSummary.accepted} zugesagt
            </span>
          )}
          {teacherSummary.declined > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              {teacherSummary.declined} abgesagt
            </span>
          )}
          {teacherSummary.pending > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              {teacherSummary.pending} offen
            </span>
          )}
        </div>
      )}

      {/* Footer note (requester / multi-request) */}
      {footerNote && <div className="text-xs text-gray-400 mt-1">{footerNote}</div>}

      {/* Cascade-Lehrer pro Split-Rolle (yellow-fix-v2, Punkt d) */}
      {(() => {
        // Gespaltene Bubble: nur die zur Rolle passende Liste.
        // Nicht-gespaltene Bubble (yellow-fix-v3, L1): alle nicht-leeren Listen.
        // assigned-fix-v1: "Übernommen von" fällt auf lessons-abgeleitete
        // assignedTeachers zurück, wenn keine Kaskaden-Daten existieren — der
        // zugewiesene Lehrer wird IMMER gezeigt (grünes Badge).
        type Entry = { code: string; rank?: number; name?: string | null }
        const acceptedFallback: Entry[] = (assignedTeachers ?? []).map((t) => ({ code: t.code, name: t.name }))
        const acceptedList: Entry[] | undefined =
          cascadeTeachers?.accepted?.length
            ? cascadeTeachers.accepted.map((t) => ({ code: t.code, rank: t.rank, name: undefined }))
            : acceptedFallback.length > 0
              ? acceptedFallback
              : undefined
        const sections: { label: string; list?: Entry[]; green?: boolean }[] = []
        if (splitRole === 'filled' || (!splitRole && (acceptedList?.length || cascadeTeachers?.accepted))) {
          sections.push({ label: 'Übernommen von', list: acceptedList, green: true })
        }
        if (splitRole === 'declined' || (!splitRole && cascadeTeachers?.declined?.length)) {
          sections.push({ label: 'Abgelehnt von', list: cascadeTeachers?.declined })
        }
        const wantsCandidates = splitRole === 'pending' || splitRole === 'open' || (!splitRole && cascadeTeachers?.candidates?.length)
        if (wantsCandidates) {
          sections.push({ label: 'Noch offen — Kontaktiert', list: cascadeTeachers?.candidates })
        }

        const visible = sections.filter(s => s.list && s.list.length > 0)
        if (visible.length === 0) return null
        const overflow = cascadeTeachers?.overflow_count ?? 0
        return (
          <div className="mt-1.5 space-y-1.5">
            {visible.map((sec) => (
              <div key={sec.label}>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">{sec.label}</div>
                <div className="flex flex-wrap gap-1">
                  {sec.list!.map((t, i) => (
                    <span
                      key={`${sec.label}-${t.code}-${i}`}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-semibold"
                      style={
                        sec.green
                          ? {
                              background: 'rgba(34,197,94,0.18)',
                              border: '1px solid rgba(34,197,94,0.45)',
                              color: '#86efac',
                            }
                          : {
                              background: 'rgba(255,255,255,0.08)',
                              border: '1px solid rgba(255,255,255,0.15)',
                              color: 'rgba(255,255,255,0.85)',
                            }
                      }
                      title={t.name ? `${t.name} — Übernommen` : t.rank !== undefined ? `Priorität ${t.rank}` : 'Übernommen'}
                    >
                      {t.code}
                      {t.rank !== undefined && <span className="text-[9px] opacity-60">#{t.rank}</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {overflow > 0 && (
              <div className="text-[11px] text-gray-400">+{overflow} weitere</div>
            )}
          </div>
        )
      })()}

      {/* rk-fix-v2 (Root Cause 2): per-Lektion Interesse/Absage aus SubstitutionResponse.
          assigned-fix-v1: zugewiesene Lehrer pro Lektion erhalten ein GRÜNES Badge
          (statt blauem Interesse) — sobald die Zuweisung steht, sieht man beim
          Hover, wer die Lektion tatsächlich übernommen hat. */}
      {(() => {
        const perLesson = cascadeTeachers?.per_lesson
        if (!perLesson) return null
        const lessonEntries = sortLessonsByDateTime(lessons)
          .map((l) => {
            const key = (l as { lesson_request_id?: string }).lesson_request_id
            return key ? { key, label: l.time_slot_name || l.date || key, data: perLesson[key] } : null
          })
          .filter((e): e is { key: string; label: string; data: { interested: string[]; declined: string[]; assigned?: string[] } } =>
            !!e && !!e.data && ((e.data.interested?.length ?? 0) > 0 || (e.data.declined?.length ?? 0) > 0 || (e.data.assigned?.length ?? 0) > 0)
          )
        if (lessonEntries.length === 0) return null
        return (
          <div className="mt-1.5 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Per Lektion</div>
            {lessonEntries.map((e) => {
              const assigned = e.data.assigned ?? []
              // Zugewiesene Lehrer bleiben nicht blau im Wartepool-Button.
              const interestedOnly = (e.data.interested ?? []).filter((c) => !assigned.includes(c))
              return (
                <div key={e.key} className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-gray-400 min-w-[3rem]">{e.label}</span>
                  {assigned.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.45)', color: '#86efac' }}
                      title="Übernommen"
                    >
                      {assigned.join(', ')}
                    </span>
                  )}
                  {interestedOnly.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: 'rgba(59,130,246,0.18)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd' }}
                      title="Interessiert"
                    >
                      {interestedOnly.join(', ')}
                    </span>
                  )}
                  {e.data.declined?.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5' }}
                      title="Abgelehnt"
                    >
                      {e.data.declined.join(', ')}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )
      })()}
    </div>
  )
}
